import { MutationTree } from "vuex";
import { ViewsState, ViewPageDimensions, ViewMode, ViewCellObject, ViewPage, UpdatedViewCellData, ViewObject } from "./types";
import Vue from "vue";
import { getDefaultState } from "./state";
import { isEqual } from "lodash";

export const mutations: MutationTree<ViewsState> = {
	setViews(state, views) {
		state.views = views;
	},

	setUserViews(state, userViews) {
		state.userViews = userViews;
	},

	setPluginTypes(state, pluginTypes) {
		state.pluginTypes = pluginTypes;
	},

	setViewMode(state, viewMode: ViewMode) {
		state.viewMode = viewMode;
	},

	setSelectedView(state, view: ViewObject) {
		state.selectedView = view;
	},

	setSelectedViewPageNumber(state, pageNumber: number) {
		state.selectedViewPageNumber = pageNumber;
	},

	setSelectedViewCells(state, viewCells: ViewCellObject[]): void {
		if (state.selectedView) {
			state.selectedView.cells = viewCells;

			let pages = state.selectedView.pages.map(viewPage => viewPage.pageNumber);

			// Update the cell contents store. Used by the plugins.
			viewCells.forEach(cell => {
				state.cellContents[cell.viewCellId] = cell.contents;
			});

			pages.forEach(pageNumber => {
				let pageNumberIndex = state.selectedView.pages.findIndex(viewPage => viewPage.pageNumber === pageNumber);

				state.selectedView.pages[pageNumberIndex].cells = viewCells.filter(viewCell => {
					return viewCell.pageNumber === pageNumber;
				})
			});
		}
	},

	setSelectedViewPublic(state, value: boolean) {
		if (state.selectedView) {
			state.selectedView.public = value;
		}
	},

	setViewDimensionsForAllPages(state, pages: ViewPage[]): void {
		pages.forEach(page =>
			mutations.setViewDimensions(state, { x: page.columns, y: page.rows, pageNumber: page.pageNumber })
		);
	},

	setViewDimensions(state, dimensions: ViewPageDimensions): void {
		if (state.selectedView) {
			// Filter displayed page's cells outside grid size
			const newPageCells: ViewCellObject[] = state.selectedView.cells.filter(
				cell => cell.pageNumber === dimensions.pageNumber && cell.x < dimensions.x && cell.y < dimensions.y
			);

			const otherPagesCells: ViewCellObject[] = state.selectedView.cells.filter(
				cell => cell.pageNumber !== dimensions.pageNumber
			);

			const page: ViewPage = state.selectedView.pages.find(p => p.pageNumber === dimensions.pageNumber);

			if (page.columns > dimensions.x) {
				// Resize cells which stretch outside grid width
				newPageCells
					.filter(cell => cell.x + cell.width > dimensions.x)
					.forEach(foundCell => (foundCell.width -= foundCell.width + foundCell.x - dimensions.x));
			}

			if (page.rows > dimensions.y) {
				// Resize cells which stretch outside grid height
				newPageCells
					.filter(cell => cell.y + cell.height > dimensions.y)
					.forEach(foundCell => (foundCell.height -= foundCell.height + foundCell.y - dimensions.y));
			}

			state.selectedView.cells = newPageCells.concat(otherPagesCells);

			page.columns = dimensions.x;
			page.rows = dimensions.y;
		}
	},

	setSelectedViewTitle(state, title: string) {
		if (state.selectedView) {
			state.selectedView.title = title;
		}
	},

	setSelectedTileType(state, tileType) {
		state.selectedTileType = tileType;
	},

	setSideBarExtended(state, extended) {
		state.sideBarExtended = extended;
	},

	setSideBarPinned(state, pinned) {
		state.sideBarPinned = pinned;
	},

	setViewSessionId(state, sessionId) {
		state.viewSessionId = sessionId;
	},

	setContents(state, { viewCellId, contents }) {
		if (viewCellId) {
			state.cellContents[viewCellId.toString()] = contents;
		}
	},

	deleteCell(state, { viewCellId }) {
		let cellIndex = state.selectedView.cells.findIndex(cell => cell.viewCellId === viewCellId);
		let cell = state.selectedView.cells[cellIndex];
		let pageNumberIndex = state.selectedView.pages.findIndex(page => page.pageNumber === cell.pageNumber);

		if (cellIndex < 0) {
			return;
		}

		Vue.delete(state.selectedView.cells, cellIndex);

		let pageCellIndex = state.selectedView.pages[pageNumberIndex].cells.findIndex(cell => cell.viewCellId === viewCellId);

		if (pageCellIndex < 0) {
			return;
		}

		Vue.delete(state.selectedView.pages[pageNumberIndex].cells, pageCellIndex);
	},

	deletePageCells(state, pageNumber) {
		let pageNumberIndex = state.selectedView.pages.findIndex(page => page.pageNumber === pageNumber);

		if (!state.selectedView.pages[pageNumberIndex]) {
			return;
		}

		state.selectedView.cells.removeWhere((cell: ViewCellObject) => {
			let pageNumberMatches = cell.pageNumber === pageNumber;
			let cellIdMatches = state.selectedView.pages[pageNumberIndex].cells.some(pageCell => {
				return pageCell.viewCellId === cell.viewCellId;
			});

			return pageNumberMatches || cellIdMatches;
		})
		state.selectedView.pages[pageNumberIndex].cells = [];
	},

	setUpdatedCellContents(state, viewCellData: UpdatedViewCellData[]) {
		if (!viewCellData) {
			return;
		}

		viewCellData.forEach(cellData => {
			let currentContents = state.cellContents[cellData.viewCellId];
			let newContents = cellData.contents || cellData.defaultContents;

			// Diff the cell contents - we only want to do an update here if we have actually made changes
			if (!isEqual(newContents, currentContents)) {
				Vue.set(state.cellContents, cellData.viewCellId, newContents);
			}
		});
	},

	resetState(state) {
		Object.assign(state, getDefaultState());
	},

	setSelectedViewHasUserGroups(state, hasUserGroups) {
		if (state.selectedView) {
			state.selectedView.hasUserGroups = hasUserGroups;
		}
	},

	updateViewPages(state, pages: ViewPage[]) {
		let view = state.views.find(view => view.viewId == pages[0].viewId);
		if (view) {
			view.pages = pages;
		}
	},

	updateSelectedViewPages(state, pages: ViewPage[]) {
		state.selectedView.pages = pages;
	}
};
